/**
 * ChatEmbedPage - Presentation Layer
 *
 * Full-page chat interface for C-Market embed access via JWT token.
 *
 * Flow:
 * 1. Verify JWT token from URL using shared useEmbedAuth hook
 * 2. Fetch chat room data with chatRoomId
 * 3. Render full-page VendorChatWindow
 * 4. Handle real-time messages
 *
 * URL: /embed/chat/:chatRoomId?token=JWT
 *
 * Clean Architecture:
 * - Uses shared useEmbedAuth hook for token verification
 * - Uses shared EmbedAuthStates for consistent UI
 * - Uses domain model conversion for type safety
 */

import { useParams } from "react-router-dom";
import { EmbedErrorState, useVendorAuth } from "../../../auth";
import { useChatRoomOperations } from "../../application/hooks/useChatOperations";
import { ChatRoom } from "../../domain/models";
import { VendorChatWindow } from "../../../../components/VendorChatWindow";
import {
  isEmbeddedInIframe,
  notifyParentEmbedClose,
} from "../../../../utils/embedMessaging";
import { ChatWindowSkeleton } from "@semo/shared-ui";

export function ChatEmbedPage() {
  const { chatRoomId } = useParams<{ chatRoomId: string }>();

  // Use authenticated user from context (guaranteed by EmbedAuthProvider)
  const { currentUser } = useVendorAuth();

  // Fetch chat room with high-level operations hook
  const {
    room: chatRoomDto,
    isLoading: roomLoading,
    error: roomError,
  } = useChatRoomOperations(chatRoomId || "");

  // Ensure user is loaded before rendering chat
  if (!currentUser) {
    return <ChatWindowSkeleton />;
  }

  // Convert DTO to domain model for type safety
  const chatRoom = chatRoomDto ? ChatRoom.fromDto(chatRoomDto) : null;

  // c-market 모달(iframe)에서 열렸으면 부모 모달을 닫고, 팝업으로 열린 경우엔 window.close().
  // (iframe 에선 window.close 가 no-op 이므로 postMessage 를 우선.)
  const handleClose = () => {
    notifyParentEmbedClose("closed");
    window.close();
  };

  // Room loading state (after auth)
  if (roomLoading) {
    return <ChatWindowSkeleton />;
  }

  // Room error state
  if (roomError) {
    return (
      <EmbedErrorState
        title="채팅방을 찾을 수 없습니다"
        message="요청하신 채팅방이 존재하지 않거나 접근 권한이 없습니다."
      />
    );
  }

  // No data state
  if (!chatRoom) {
    return (
      <EmbedErrorState
        title="데이터 없음"
        message="채팅방 데이터를 불러올 수 없습니다."
      />
    );
  }

  // Render full-page chat window
  return (
    <div className="h-viewport w-full flex flex-col bg-slate-100  overflow-hidden">
      <VendorChatWindow
        chatId={chatRoom.data.id}
        customerName={chatRoom.getCustomerName()}
        onClose={handleClose}
        variant="full"
        supplierId={currentUser.id}
        hideClose={isEmbeddedInIframe()}
      />
    </div>
  );
}
